import { InvestmentActivity, resolveInvestmentTransactionKind } from '@orn/global/investments/investmentActivity.js';

import { firstLetterCapitalize, toCurrency } from '../formatting';

const UNOFFICIAL_CURRENCY_MAX_FRACTION_DIGITS = 8;

export type InvestmentAmountTone = 'primary' | 'success';

export interface IInvestmentCurrency {
  currencyCode?: string | null;
  /** Set instead of the ISO code for a currency with none, such as a cryptocurrency. */
  unofficialCurrencyCode?: string | null;
}

export interface IInvestmentAmountInput extends IInvestmentCurrency {
  /** Major units, in the provider's sign: positive when cash leaves the account. */
  amount: number;
  activity: InvestmentActivity;
}

export interface IInvestmentAmountDisplay {
  text: string;
  tone: InvestmentAmountTone;
}

/** The row's sentence-case label: the provider's subtype, or "Canceled" for a cancellation. */
export function investmentTransactionLabel(type: string, subtype: string): string {
  return firstLetterCapitalize(resolveInvestmentTransactionKind(type, subtype));
}

/** A figure in the transaction's currency. One with no ISO code reads as the bare number beside its code, never "$". */
export function investmentMoneyText(
  value: number,
  { currencyCode, unofficialCurrencyCode }: IInvestmentCurrency
): string {
  // A coin amount can sit well below a hundredth of a unit.
  return !currencyCode && unofficialCurrencyCode
    ? `${toCurrency(value, { symbol: false, maxFractionDigits: UNOFFICIAL_CURRENCY_MAX_FRACTION_DIGITS })} ${unofficialCurrencyCode}`
    : toCurrency(value, { currencyCode });
}

/**
 * The amount cell. A trade is neutral, so a sell never reads as income; any
 * other row is signed and toned only when cash arrives in the account.
 */
export function investmentAmountDisplay({
  amount,
  activity,
  ...currency
}: IInvestmentAmountInput): IInvestmentAmountDisplay {
  const isCashArriving = activity !== InvestmentActivity.TRADE && amount < 0;
  const sign = isCashArriving ? '+' : '';

  return {
    text: `${sign}${investmentMoneyText(Math.abs(amount), currency)}`,
    tone: isCashArriving ? 'success' : 'primary',
  };
}
